import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';

import Editor, { Monaco, OnMount } from '@monaco-editor/react';
import { useAuth } from 'contexts/AuthContext';

import { EditorViewMode } from 'components/editor/ViewModeToggle';
import { WorkSpaceNode, getWorkSpace } from 'stores/WorkSpaceStoreAdapter';

import { KnzToolbar } from './KnzToolbar';
import { KnzViewer } from './KnzViewer';
import { setCurrentUserId } from './embeds/articleSuggestStore';
import { setLinkFiles } from './embeds/linkFileStore';
import { registryLookup } from './embeds/registry';
import {
  KNZ_LANGUAGE_ID,
  registerKnzLanguage,
  resolveLinkFileIdAt,
  setMentionUsers,
} from './monaco/knzLanguage';
import { setKnzMarkers } from './monaco/markers';
import { KnzCodeEditor } from './monaco/types';
import { parseKnz } from './parser';

/**
 * ディレクトリパスを `/` 区切りで組み立てる。ルート直下（親なし）は `/`。
 * 長い場合は先頭を三点リーダーで省略する（末尾側を残す）。
 */
const truncatePath = (segments: string[], maxLen = 40): string => {
  if (segments.length === 0) return '/';
  const full = `/${segments.join('/')}`;
  if (full.length <= maxLen) return full;
  // 末尾から詰めて、収まらない分を … にする
  let acc = '';
  for (let i = segments.length - 1; i >= 0; i--) {
    const next = acc ? `${segments[i]}/${acc}` : segments[i];
    if (next.length > maxLen) {
      return `…/${acc}`;
    }
    acc = next;
  }
  return `/${acc}`;
};

/**
 * ワークスペースのフラットノード配列から、link 補完用のファイル一覧を作る。
 * ファイル（type=FILE かつ fileId あり）だけを対象に、parentId を辿って
 * ディレクトリパスを組み立てる（長い場合は truncatePath で省略）。
 */
const buildLinkFiles = (
  nodes: WorkSpaceNode[],
): { fileId: string; name: string; dirPath: string }[] => {
  const byId = new Map<string, WorkSpaceNode>();
  nodes.forEach((n) => byId.set(n.id, n));

  /** あるノードの親フォルダ名の配列（ルート→直近の順） */
  const dirSegments = (node: WorkSpaceNode): string[] => {
    const segs: string[] = [];
    let pid = node.parentId;
    // 循環対策に訪問済みを持つ
    const visited = new Set<string>();
    while (pid && byId.has(pid) && !visited.has(pid)) {
      visited.add(pid);
      const parent = byId.get(pid)!;
      segs.unshift(parent.name);
      pid = parent.parentId;
    }
    return segs;
  };

  return nodes
    .filter((n) => n.type === 'FILE' && !!n.fileId)
    .map((n) => ({
      fileId: n.fileId as string,
      name: n.name,
      dirPath: truncatePath(dirSegments(n)),
    }));
};

type Props = {
  value: string;
  /** 表示モード（edit / split / preview） */
  viewMode: EditorViewMode;
  /** 編集不可 */
  readOnly: boolean;
  onChange: (value: string) => void;
  /** 表示モードを変更する */
  onViewModeChange: (mode: EditorViewMode) => void;
  onSave?: () => void;
  /**
   * CodeBlock の link 参照（`link:<fileId>`）で定義ジャンプ（F12 / Cmd/Ctrl+B）したときに、
   * その fileId のファイルを開いてアクティブ化するコールバック。ワークスペース側が実装する。
   */
  onOpenLinkedFile?: (fileId: string) => void;
  /** 記事ヘッダー用のファイル日時 */
  createdAt?: string;
  updatedAt?: string;
};

/**
 * knz 専用エディタ。
 * - edit: ツールバー + Monaco
 * - split: 左 Monaco / 右 KnzViewer（左右分割）
 * - preview: KnzViewer（ツールバーは残す）
 */
export const KnzEditor: React.FC<Props> = ({
  value,
  viewMode,
  readOnly,
  onChange,
  onViewModeChange,
  onSave,
  onOpenLinkedFile,
  createdAt,
  updatedAt,
}) => {
  const { mode, systemMode } = useColorScheme();
  const resolved = mode === 'system' ? systemMode : mode;
  const isDark = resolved === 'dark';

  // mention 補完用にユーザー一覧（全ユーザー）を Monaco 補完へ渡す。
  // userId で重複排除してから渡す（重複があると補完候補が欠落・重複する）。
  const { userSession, users } = useAuth();
  React.useEffect(() => {
    const seen = new Set<string>();
    const unique: { userId: string; userName: string }[] = [];
    users.forEach((u) => {
      if (seen.has(u.userId)) return;
      seen.add(u.userId);
      unique.push({ userId: u.userId, userName: u.userName });
    });
    setMentionUsers(unique);
  }, [users]);

  // ArticleNav の articleId 補完（self / articles）用に、現在ユーザーの userId を渡す。
  // 候補を「自分が著者の記事のみ」に絞るため（仕様 8.5.1.1）。
  React.useEffect(() => {
    setCurrentUserId(userSession.userId);
  }, [userSession.userId]);

  // CodeBlock の link 補完用に、マイワークスペースの全ファイル一覧を取得して Monaco 補完へ渡す。
  React.useEffect(() => {
    let active = true;
    getWorkSpace()
      .then((nodes) => {
        if (active) setLinkFiles(buildLinkFiles(nodes));
      })
      .catch(() => {
        // 取得失敗時は link 補完なし（致命的ではない）
        if (active) setLinkFiles([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // ツールバーの表示/非表示
  const [toolbarVisible, setToolbarVisible] = React.useState(true);

  // 分割/プレビューの再描画を軽くするデバウンス値
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), 250);
    return () => clearTimeout(timer);
  }, [value]);

  const editorRef = React.useRef<KnzCodeEditor | null>(null);
  const monacoRef = React.useRef<Monaco | null>(null);

  const onSaveRef = React.useRef(onSave);
  const viewModeRef = React.useRef(viewMode);
  const onViewModeChangeRef = React.useRef(onViewModeChange);
  React.useEffect(() => {
    onSaveRef.current = onSave;
    viewModeRef.current = viewMode;
    onViewModeChangeRef.current = onViewModeChange;
  }, [onSave, viewMode, onViewModeChange]);

  /** Cmd/Ctrl+P: プレビュー切替（edit⇄preview、split は preview へ） */
  const togglePreviewMode = React.useCallback(() => {
    onViewModeChangeRef.current(viewModeRef.current === 'preview' ? 'edit' : 'preview');
  }, []);

  /** Cmd/Ctrl+\\: 分割トグル（split⇄edit） */
  const toggleSplitMode = React.useCallback(() => {
    onViewModeChangeRef.current(viewModeRef.current === 'split' ? 'edit' : 'split');
  }, []);

  const handleBeforeMount = React.useCallback((monaco: Monaco) => {
    registerKnzLanguage(monaco);
  }, []);

  const updateMarkers = React.useCallback((monaco: Monaco, ed: KnzCodeEditor) => {
    const model = ed.getModel();
    if (!model) return;
    const result = parseKnz(model.getValue(), registryLookup);
    setKnzMarkers(monaco, model, result.diagnostics);
  }, []);

  const onOpenLinkedFileRef = React.useRef(onOpenLinkedFile);
  React.useEffect(() => {
    onOpenLinkedFileRef.current = onOpenLinkedFile;
  }, [onOpenLinkedFile]);

  /**
   * 定義ジャンプ（F12 / Cmd/Ctrl+B）。
   * - カーソル行が CodeBlock の `link:<fileId>` なら、別ファイルを開いてアクティブ化する
   *   （onOpenLinkedFile）。DefinitionProvider は別ファイルを扱えないためここで分岐する。
   * - それ以外（children の `-id` など）は Monaco 標準の定義ジャンプに委譲し、
   *   同一ファイル内の定義ブロック開始行へ移動する。
   */
  const handleGoToDefinition = React.useCallback(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const model = ed.getModel();
    const position = ed.getPosition();
    if (!model || !position) return;
    const fileId = resolveLinkFileIdAt(model.getValue(), position.lineNumber - 1);
    if (fileId) {
      onOpenLinkedFileRef.current?.(fileId);
      return;
    }
    // children id 等は標準の定義ジャンプ（DefinitionProvider）へ委譲する
    ed.trigger('knz', 'editor.action.revealDefinition', null);
  }, []);

  const handleMount = React.useCallback<OnMount>(
    (ed, monaco) => {
      editorRef.current = ed;
      monacoRef.current = monaco;

      ed.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => onSaveRef.current?.());
      ed.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP, () => togglePreviewMode());
      ed.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Backslash, () => toggleSplitMode());
      // 定義ジャンプ: F12 と Cmd/Ctrl+B の両方でジャンプできるようにする
      ed.addCommand(monaco.KeyCode.F12, () => handleGoToDefinition());
      ed.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyB, () => handleGoToDefinition());

      updateMarkers(monaco, ed);
    },
    [updateMarkers, togglePreviewMode, toggleSplitMode, handleGoToDefinition],
  );

  // 内容変更時に marker を更新（デバウンス）
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleChange = (v: string | undefined) => {
    const next = v ?? '';
    onChange(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const monaco = monacoRef.current;
      const ed = editorRef.current;
      if (monaco && ed) updateMarkers(monaco, ed);
    }, 250);
  };

  // プレビュー単独モード時は Monaco が無いので window でショートカットを拾う
  React.useEffect(() => {
    if (viewMode !== 'preview') return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        togglePreviewMode();
      } else if (e.key === '\\') {
        e.preventDefault();
        toggleSplitMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [viewMode, togglePreviewMode, toggleSplitMode]);

  const viewer = (
    <Box sx={{ height: '100%', overflow: 'auto', bgcolor: 'background.paper' }}>
      <KnzViewer
        source={debouncedValue}
        createdAt={createdAt}
        updatedAt={updatedAt}
        tocMode="ignore"
      />
    </Box>
  );

  const monacoEditor = (
    <Editor
      height="100%"
      language={KNZ_LANGUAGE_ID}
      value={value}
      theme={isDark ? 'knz-dark' : 'knz-light'}
      beforeMount={handleBeforeMount}
      onMount={handleMount}
      onChange={handleChange}
      options={{
        readOnly,
        minimap: { enabled: true },
        fontSize: 13,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 1,
        wordWrap: 'on',
        quickSuggestions: { other: true, comments: true, strings: true },
        suggestOnTriggerCharacters: true,
        wordBasedSuggestions: 'off',
      }}
    />
  );

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {toolbarVisible ? (
        <KnzToolbar
          getEditor={() => editorRef.current}
          onHide={() => setToolbarVisible(false)}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
        />
      ) : (
        <Tooltip title="ツールバーを表示">
          <Box
            onClick={() => setToolbarVisible(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              cursor: 'pointer',
              borderBottom: 1,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">ツールバー</Typography>
          </Box>
        </Tooltip>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {viewMode !== 'preview' && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              borderRight: viewMode === 'split' ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            {monacoEditor}
          </Box>
        )}
        {viewMode !== 'edit' && <Box sx={{ flex: 1, minWidth: 0 }}>{viewer}</Box>}
      </Box>
    </Box>
  );
};

export default KnzEditor;
